import React, { useMemo } from "react";
import { cn } from "@jem-open/jem-ui";
import { PreviewParams } from "./types";
import { fmtShortDate } from "./previewHelpers";
import PreviewHeader from "./PreviewHeader";
import SummaryTile from "./SummaryTile";
import { Td, Th } from "./tableHelpers";
import { PlannedVsActualsPreviewData } from "../../services/model/ReportPreviewModels";

interface PlannedVsActualsPreviewProps {
  params: PreviewParams;
  data: PlannedVsActualsPreviewData;
  onDownload: () => void;
}

const PlannedVsActualsPreview = ({
  params,
  data,
  onDownload,
}: PlannedVsActualsPreviewProps): JSX.Element => {
  const dailyRows = useMemo(
    () =>
      data.daily.slice(0, 14).map((d) => ({
        date: new Date(`${d.record_date}T00:00:00`),
        rostered: d.rostered,
        present: d.present,
        schedHrs: d.scheduled_hours,
        actualHrs: d.actual_hours,
        estimatedHrs: d.estimated_hours ?? d.actual_hours,
        attPct: Math.round(d.attendance_pct),
      })),
    [data.daily]
  );

  const { totals } = data;
  const totalsEstimated = totals.estimated_hours ?? totals.actual_hours;
  const avgAttendance = Math.round(totals.attendance_pct);

  return (
    <>
      <PreviewHeader
        title="Planned vs Actuals"
        subtitle="Operational attendance report"
        params={params}
        onDownload={onDownload}
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-sm mb-lg">
        <SummaryTile
          label="Rostered"
          value={String(totals.rostered)}
          unit="shifts"
        />
        <SummaryTile
          label="Present"
          value={String(totals.present)}
          unit="clocked in"
        />
        <SummaryTile
          label="Attendance"
          value={`${avgAttendance}%`}
          unit="avg"
          flag={avgAttendance < 50}
        />
        <SummaryTile
          label="Confirmed Hours"
          value={`${Math.round(totals.actual_hours)} / ${Math.round(
            totals.scheduled_hours
          )}`}
          unit="confirmed / sched"
        />
        <SummaryTile
          label="Estimated Hours"
          value={
            totals.estimated_hours != null
              ? `${Math.round(totals.estimated_hours)} / ${Math.round(
                  totals.scheduled_hours
                )}`
              : "—"
          }
          unit="estimated / sched"
        />
      </div>

      <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">
        Daily breakdown
      </h2>
      {dailyRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-greyscale-border p-md text-sm text-greyscale-text-caption mb-lg">
          No scheduled shifts for the selected range.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-xs mb-lg">
          {dailyRows.map((r) => (
            <div
              key={r.date.toISOString()}
              className={cn(
                "rounded-lg border p-sm",
                r.attPct < 50
                  ? "border-red-300 bg-red-50"
                  : "border-greyscale-border bg-white"
              )}
            >
              <p className="text-xs uppercase tracking-wide text-greyscale-text-caption">
                {fmtShortDate(r.date)}
              </p>
              <p className="text-lg font-semibold text-greyscale-text-title mt-xxxs">
                {r.attPct}%
              </p>
              <p className="text-xs text-greyscale-text-body">
                {r.present} of {r.rostered}
              </p>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                {Math.round(r.actualHrs)}h / {Math.round(r.schedHrs)}h
              </p>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">
        Daily detail
      </h2>
      <div className="overflow-x-auto rounded-lg border border-greyscale-border">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-greyscale-border">
            <tr>
              <Th>Date</Th>
              <Th align="right">Rostered</Th>
              <Th align="right">Present</Th>
              <Th align="right">Att %</Th>
              <Th align="right">Sched Hrs</Th>
              <Th align="right">Actual Hrs</Th>
              <Th align="right">Est. Hrs</Th>
            </tr>
          </thead>
          <tbody>
            {dailyRows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-sm py-md text-center text-sm text-greyscale-text-caption"
                >
                  No data for the selected range.
                </td>
              </tr>
            ) : (
              dailyRows.map((r) => (
                <tr
                  key={r.date.toISOString()}
                  className="border-b border-greyscale-border last:border-0"
                >
                  <Td className="text-greyscale-text-title">
                    {fmtShortDate(r.date)}
                  </Td>
                  <Td align="right">{r.rostered}</Td>
                  <Td align="right">{r.present}</Td>
                  <Td
                    align="right"
                    className={cn(
                      "font-semibold",
                      r.attPct < 50
                        ? "text-red-700"
                        : "text-greyscale-text-title"
                    )}
                  >
                    {r.attPct}%
                  </Td>
                  <Td align="right">{Math.round(r.schedHrs)}h</Td>
                  <Td align="right">{Math.round(r.actualHrs)}h</Td>
                  <Td align="right">
                    {r.estimatedHrs > r.actualHrs
                      ? `${r.estimatedHrs.toFixed(2)}h`
                      : "—"}
                  </Td>
                </tr>
              ))
            )}
            {dailyRows.length > 0 && (
              <tr className="bg-neutral-50 font-semibold">
                <Td className="text-greyscale-text-title">Total</Td>
                <Td align="right">{totals.rostered}</Td>
                <Td align="right">{totals.present}</Td>
                <Td align="right">{avgAttendance}%</Td>
                <Td align="right">{Math.round(totals.scheduled_hours)}h</Td>
                <Td align="right">{Math.round(totals.actual_hours)}h</Td>
                <Td align="right">
                  {totalsEstimated > totals.actual_hours
                    ? `${totalsEstimated.toFixed(2)}h`
                    : "—"}
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PlannedVsActualsPreview;
